import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellSlash, IdentificationCard } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmtDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

function DaysBadge({ days }) {
  const cls = days <= 7 ? "bg-red-100 text-red-700 border-red-200"
    : days <= 30 ? "bg-orange-100 text-orange-700 border-orange-200"
      : "bg-emerald-100 text-emerald-700 border-emerald-200";
  const txt = days < 0 ? `Scaduto da ${-days} gg` : days === 0 ? "Scade oggi" : `${days} gg`;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>{txt}</span>;
}

// Documenti personali in scadenza del cliente (immobile della pratica).
export default function PersonalDocDeadlines({ propertyId }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    api.get("/properties/upcoming-deadlines", { params: { days: 365, property_id: propertyId, include_muted: true } })
      .then((r) => setItems((r.data?.items || []).filter((s) => s.categoria === "scadenza_documenti_personali")))
      .catch(() => setItems([]));
  }, [propertyId]);

  const toggle = async (s) => {
    setBusy(s.scadenza_id);
    try {
      await api.post(`/properties/${s.property_id}/scadenze/${s.scadenza_id}/${s.muted ? "unmute" : "mute"}`);
      setItems((list) => list.map((x) => (x.scadenza_id === s.scadenza_id ? { ...x, muted: !s.muted } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(null);
    }
  };

  if (!propertyId || items === null) return null;
  return (
    <div className="kpi-card" data-testid="personal-doc-deadlines">
      <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
        <IdentificationCard size={20} className="text-purple-600" /> Documenti personali in scadenza
      </h3>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="personal-doc-deadlines-empty">Nessun documento personale in scadenza.</p>
      ) : (
        <ul className="divide-y">
          {items.map((s) => (
            <li key={s.scadenza_id || `${s.descrizione}-${s.data_scadenza}`} className={`flex items-center gap-3 py-2.5 ${s.muted ? "opacity-60" : ""}`}
              data-testid={`personal-doc-deadline-${s.scadenza_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.descrizione}</div>
                <div className="text-xs text-muted-foreground">Scade il {fmtDate(s.data_scadenza)}</div>
              </div>
              <DaysBadge days={s.days_remaining} />
              {s.scadenza_id && (
                <button
                  type="button"
                  onClick={() => toggle(s)}
                  disabled={busy === s.scadenza_id}
                  className={`p-1.5 rounded-full transition-colors ${s.muted ? "text-slate-400 hover:bg-slate-100" : "text-purple-600 hover:bg-purple-50"}`}
                  title={s.muted ? "Riattiva avvisi" : "Silenzia avvisi"}
                  aria-label={s.muted ? "Riattiva avvisi" : "Silenzia avvisi"}
                  data-testid={`personal-doc-mute-${s.scadenza_id}`}
                >
                  {s.muted ? <BellSlash size={18} /> : <Bell size={18} weight="fill" />}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
